/**
 * LeaderboardSystem — keeps this browser's scoreboard entry live.
 *
 * Tracks the current playthrough (meta.scoreProgress: best biomass per
 * level, DNA, achievements, level finish times), folds it into the
 * browser's best-ever record (saveManager.getPlayer().best) whenever it
 * beats it, and sends that best to the server every 30s while it changes,
 * immediately on reaching a goal, and via beacon when the tab is hidden.
 *
 * Playthroughs started by jumping to a level (or touched by a dev cheat)
 * are unranked: nothing about them is tracked or sent.
 *
 * Installed once at boot; lives for the whole session.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import { computeScore, isScoredStage, GOALS, PAR_SEC } from '@/score/formula';
import { submitProgress, beaconProgress, type ProgressSubmission } from '@/net/leaderboardApi';

const SUBMIT_EVERY_MS = 30_000;

export class LeaderboardSystem {
  private dirty = false;
  private sending = false;

  constructor() {
    this.adoptLegacyMulticellRun();
    saveManager.recordBest();

    bus.on('biomass:changed', ({ value }) => {
      if (!this.tracking()) return;
      const p = saveManager.getMeta().scoreProgress;
      if (saveManager.getRun().stageId === 'cell') {
        if (value <= p.cellMaxBiomass) return;
        p.cellMaxBiomass = Math.floor(value);
      } else {
        if (value <= p.multicellMaxBiomass) return;
        p.multicellMaxBiomass = Math.floor(value);
      }
      this.changed();
    });
    bus.on('dna:changed', ({ delta }) => {
      if (delta <= 0 || !this.tracking()) return;
      saveManager.getMeta().scoreProgress.dna += delta;
      this.changed();
    });
    bus.on('achievement:earned', () => {
      if (!this.tracking()) return;
      saveManager.getMeta().scoreProgress.achievements += 1;
      this.changed();
    });
    bus.on('score:goalReached', ({ stageId }) => this.handleGoal(stageId));

    setInterval(() => { if (this.dirty) void this.submit(false); }, SUBMIT_EVERY_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden' || !this.dirty) return;
      const payload = this.payload();
      if (!payload) return;
      beaconProgress(payload);
      this.dirty = false;
    });

    // Re-send on launch so the board catches anything a failed send missed.
    if (this.payload()) void this.submit(false);
  }

  /** Scored stage, in a playthrough that started with New Game and stayed clean. */
  private tracking(): boolean {
    return isScoredStage(saveManager.getRun().stageId) && !saveManager.getMeta().scoreProgress.unranked;
  }

  /** Current playthrough moved — only worth sending if it improved the best. */
  private changed(): void {
    if (saveManager.recordBest()) this.dirty = true;
  }

  private handleGoal(stageId: string): void {
    if (!isScoredStage(stageId)) return;
    const p = saveManager.getMeta().scoreProgress;
    if (p.unranked) {
      bus.emit('score:submitted', { stageId, score: computeScore(p), rank: null, status: 'unranked' });
      return;
    }
    const time = Math.round(saveManager.getRun().scoreTime * 10) / 10;
    if (stageId === 'cell' && p.cellTimeSec == null) {
      p.cellTimeSec = time;
      p.cellMaxBiomass = Math.max(p.cellMaxBiomass, GOALS.cell);
    } else if (stageId === 'multicell' && p.multicellTimeSec == null) {
      p.multicellTimeSec = time;
      p.multicellMaxBiomass = Math.max(p.multicellMaxBiomass, GOALS.multicell);
    } else {
      return; // already recorded (reload past the goal / re-cross after death)
    }
    saveManager.recordBest();
    saveManager.save();
    void this.submit(true, stageId);
  }

  /** The browser's best-ever progress, or null if there is nothing to show yet. */
  private payload(): ProgressSubmission | null {
    const player = saveManager.getPlayer();
    if (player.best.cellMaxBiomass <= 0) return null;
    return { ...player.best, playerId: player.playerId, name: player.playerName };
  }

  /** `announceStage` = emit score:submitted for that stage's result UI. */
  private async submit(announce: boolean, announceStage = ''): Promise<void> {
    const payload = this.payload();
    if (!payload) return;
    if (this.sending) { this.dirty = true; return; }
    this.sending = true;
    this.dirty = false;
    try {
      const res = await submitProgress(payload);
      if (announce) bus.emit('score:submitted', { stageId: announceStage, score: res.score, rank: res.rank, status: 'ok' });
    } catch (err) {
      console.warn('[Leaderboard] submit failed, will retry', err);
      this.dirty = true;
      if (announce) {
        bus.emit('score:submitted', { stageId: announceStage, score: computeScore(payload), rank: null, status: 'offline' });
      }
    } finally {
      this.sending = false;
    }
  }

  /**
   * Saves from before the unified board (v9) that are already mid-Multicell
   * have no level-1 finish recorded. Credit it at par — unless that run was
   * flagged unranked (it jumped straight to Multicell).
   */
  private adoptLegacyMulticellRun(): void {
    const run = saveManager.getRun() as { stageId: string; unranked?: boolean };
    const p = saveManager.getMeta().scoreProgress;
    if (run.stageId !== 'multicell' || p.cellTimeSec != null || p.unranked) return;
    if (run.unranked) {
      p.unranked = true;
    } else {
      p.cellTimeSec = PAR_SEC.cell;
      p.cellMaxBiomass = GOALS.cell;
    }
    delete run.unranked;
    saveManager.save();
  }
}
